// src/components/FileUpload.js - Enhanced with close button and HTML support
import React, { useState, useRef, useEffect } from 'react';
import './FileUpload.css';
import FilePreview from './FilePreview';
// We avoid importing external libraries directly 

/**
 * Enhanced FileUpload component with close button and improved styling
 * Designed to work reliably in incognito mode
 * UPDATED: Simplified metadata display to only show high-confidence elements
 * UPDATED: Added support for HTML files
 */
function FileUpload({ 
  onFilesAdded, 
  onError, 
  existingFiles = [], 
  maxFiles = 10,
  maxSizePerFile = 25 * 1024 * 1024,
  maxTotalSize = 100 * 1024 * 1024,
  resetFlag = false,
  onClose // New prop to handle close action
}) {
  const [files, setFiles] = useState(existingFiles);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState({});
  const [totalSize, setTotalSize] = useState(
    existingFiles.reduce((sum, file) => sum + file.size, 0)
  );
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);
  const [lastResetFlag, setLastResetFlag] = useState(resetFlag);

  // Detect if running in incognito mode
  const [isIncognitoMode, setIsIncognitoMode] = useState(false);
  
  useEffect(() => {
    const detectIncognito = () => {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
      } catch (e) {
        return true;
      }
      if (window.chrome && window.chrome.runtime) {
        try {
          if (!window.chrome.runtime.id) {
            return true;
          }
        } catch (e) {
          return true;
        }
      }
      if (navigator.storage && navigator.storage.persist) {
        try {
          const isPersisted = navigator.storage.persisted();
          if (isPersisted === false) return true;
        } catch (e) {
          return true;
        }
      }
      return false;
    };
    setIsIncognitoMode(detectIncognito());
  }, []);

  // Reset files when resetFlag changes
  useEffect(() => {
    if (resetFlag !== lastResetFlag) {
      console.log("FileUpload: Reset flag changed, clearing files");
      setFiles([]);
      setTotalSize(0);
      setLastResetFlag(resetFlag);
      setError(null);
      setProcessingStatus({});
      setPreviewFile(null);
      setIsDragging(false);
      setIsProcessing(false);
      
      // Notify parent component about cleared files
      if (onFilesAdded) {
        onFilesAdded([]);
      }
    }
  }, [resetFlag, lastResetFlag, onFilesAdded]);

  // Update local state when existingFiles prop changes
  useEffect(() => {
    if (resetFlag === lastResetFlag && existingFiles.length > 0) {
      console.log("FileUpload: existingFiles changed, updating state");
      
      // Only add files that aren't already in our state
      const newFiles = existingFiles.filter(newFile => 
        !files.some(existingFile => 
          existingFile.name === newFile.name && existingFile.size === newFile.size
        )
      );
      
      if (newFiles.length > 0) {
        const updatedFiles = [...files, ...newFiles];
        setFiles(updatedFiles);
        setTotalSize(updatedFiles.reduce((sum, file) => sum + file.size, 0));
      }
    }
  }, [existingFiles, files, resetFlag, lastResetFlag]);

  // Handle file preview
  const openFilePreview = (file) => {
    try {
      if (!file) {
        setError(`Unable to preview: File is undefined`);
        return;
      }
      
      setPreviewFile({...file});
    } catch (err) {
      console.error("Error opening file preview:", err);
      setError(`Preview error: ${err.message}`);
    }
  };

  const closeFilePreview = () => {
    setPreviewFile(null);
  };

  // Handle close button click - NEW FUNCTION
  const handleClose = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    
    // Call the onClose prop if it exists
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  // Handle error display
  useEffect(() => {
    if (error) {
      // Bubble up error to parent component
      if (onError) onError(error);
      
      const timer = setTimeout(() => {
        setError(null);
        if (onError) onError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, onError]);

  // Drag and drop handlers
  const handleDragOver = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    setIsDragging(false);
    if (isProcessing) return;
    
    // Get files from drop event
    const droppedFiles = e && e.dataTransfer && e.dataTransfer.files ? 
      Array.from(e.dataTransfer.files) : [];
    
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  };

  // Handle file selection from input
  const handleFileSelect = (e) => {
    if (isProcessing) return;
    
    // Get files from input element
    const selectedFiles = e && e.target && e.target.files ? 
      Array.from(e.target.files) : [];
    
    if (selectedFiles.length > 0) {
      processFiles(selectedFiles);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get size limit based on file type
  const getFileSizeLimit = (fileType, fileName) => {
    if (fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) return 15 * 1024 * 1024;
      else if (extension === 'pdf') return 20 * 1024 * 1024;
      else if (['csv', 'xlsx', 'xls', 'ods'].includes(extension)) return 20 * 1024 * 1024;
      else if (extension === 'docx') return 20 * 1024 * 1024;
      else if (['html', 'htm'].includes(extension)) return 10 * 1024 * 1024; // HTML file limit
    }
    if (fileType.startsWith('image/')) return 15 * 1024 * 1024;
    else if (fileType === 'application/pdf') return 20 * 1024 * 1024;
    else if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv') return 20 * 1024 * 1024;
    else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 20 * 1024 * 1024;
    else if (fileType === 'text/html') return 10 * 1024 * 1024; // HTML file limit
    else return maxSizePerFile;
  };

  // Process files for upload
  const processFiles = async (newFiles) => {
    if (!newFiles || newFiles.length === 0) return;
    
    // Check file count limit
    if (files.length + newFiles.length > maxFiles) {
      const errorMsg = `You can upload a maximum of ${maxFiles} files.`;
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }
    
    // Check total size limit
    const currentTotalSize = files.reduce((sum, file) => sum + file.size, 0);
    const newFilesTotalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
    
    if (currentTotalSize + newFilesTotalSize > maxTotalSize) {
      const errorMsg = `Total upload size cannot exceed ${Math.round(maxTotalSize/1024/1024)}MB.`;
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }
    
    setIsProcessing(true);
    const newProcessingStatus = {};
    
    // Set initial processing status for each file
    newFiles.forEach(file => {
      newProcessingStatus[file.name] = { progress: 0, status: 'Processing...' };
    });
    
    setProcessingStatus(newProcessingStatus);
    
    try {
      const processedFiles = [];
      
      for (const file of newFiles) {
        // Update status to "checking file size"
        setProcessingStatus(prev => ({
          ...prev,
          [file.name]: { ...prev[file.name], status: 'Checking file size...', progress: 10 }
        }));
        
        // Check file type and extension
        if (file.name.endsWith('.doc') && !file.name.endsWith('.docx')) {
          throw new Error(`Sorry, we only support .docx files. Please convert '${file.name}' to .docx format.`);
        }
        
        // Check file size against limit for its type
        const fileSizeLimit = getFileSizeLimit(file.type, file.name);
        
        if (file.size > fileSizeLimit) {
          let typeLabel = 'files';
          if (file.name) {
            const extension = file.name.split('.').pop().toLowerCase();
            if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) typeLabel = 'images';
            else if (extension === 'pdf') typeLabel = 'PDFs';
            else if (['csv', 'xlsx', 'xls', 'ods'].includes(extension)) typeLabel = 'spreadsheets';
            else if (extension === 'docx') typeLabel = 'DOCX files';
            else if (['html', 'htm'].includes(extension)) typeLabel = 'HTML files';
          }
          throw new Error(`File ${file.name} exceeds the ${Math.round(fileSizeLimit/1024/1024)}MB size limit for ${typeLabel}.`);
        }
        
        // Update status to "reading file content"
        setProcessingStatus(prev => ({
          ...prev,
          [file.name]: { ...prev[file.name], status: 'Reading file contents...', progress: 20 }
        }));
        
        // Process the file based on its type
        try {
          const fileInfo = await processFileContent(file, (progress) => {
            // Update progress as file is processed
            setProcessingStatus(prev => ({
              ...prev,
              [file.name]: { ...prev[file.name], progress: 20 + (progress * 80) }
            }));
          });
          
          // Add processed file to the list
          processedFiles.push({
            name: file.name,
            type: file.type,
            size: file.size,
            ...fileInfo
          });
        } catch (fileErr) {
          console.error(`Error processing file ${file.name}:`, fileErr);
          
          // Create a basic file object with minimal information
          processedFiles.push({
            name: file.name,
            type: file.type,
            size: file.size,
            error: fileErr.message
          });
        }
      }
      
      // Update files state with newly processed files
      const updatedFiles = [...files, ...processedFiles];
      setFiles(updatedFiles);
      setTotalSize(updatedFiles.reduce((sum, file) => sum + file.size, 0));
      
      // Notify parent component about updated files
      if (onFilesAdded) {
        console.log("FileUpload: Notifying parent of added files", updatedFiles);
        onFilesAdded(updatedFiles);
      }
      
      // Clear processing status
      setProcessingStatus({});
    } catch (err) {
      console.error("File processing error:", err);
      setError(err.message || "Error processing files");
      if (onError) onError(err.message || "Error processing files");
      setProcessingStatus({});
    } finally {
      setIsProcessing(false);
    }
  };

  // Process file content based on file type
  const processFileContent = async (file, progressCallback) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      // Add timeout to prevent hanging
      const timeoutId = setTimeout(() => {
        reader.abort();
        reject(new Error(`Processing timeout for file: ${file.name}`));
      }, 30000); // 30 second timeout
      
      // Progress event handler
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = event.loaded / event.total;
          if (progressCallback) progressCallback(progress);
        }
      };
      
      // Load complete handler
      reader.onload = async (event) => {
        clearTimeout(timeoutId);
        
        try {
          // Identify file type
          if (file.type.startsWith('image/')) {
            // Process image file
            processImage(file, event.target.result, resolve, reject);
          } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
            // Process PDF file
            processPdf(file, event.target.result, resolve);
          } else if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
            // Process CSV file
            processCsv(file, event.target.result, resolve);
          } else if (file.type.includes('spreadsheet') || 
                    file.type.includes('excel') || 
                    file.name.match(/\.xlsx?$/i) ||
                    file.name.match(/\.ods$/i)) {
            // Process Excel file
            processExcel(file, event.target.result, resolve);
          } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                    file.name.toLowerCase().endsWith('.docx')) {
            // Process DOCX file
            processDocx(file, event.target.result, resolve);
          } else if (file.type === 'text/html' || 
                    file.name.toLowerCase().endsWith('.html') || 
                    file.name.toLowerCase().endsWith('.htm')) {
            // Process HTML file - new handler
            processHtml(file, event.target.result, resolve);
          } else if (file.type.startsWith('text/') || 
                    file.name.match(/\.(js|py|java|cpp|css|json|md|txt)$/i)) {
            // Process text file
            processText(file, event.target.result, resolve);
          } else {
            // Process other binary file types
            processBinary(file, event.target.result, resolve);
          }
        } catch (err) {
          reject(new Error(`Failed to process file: ${file.name} - ${err.message}`));
        }
      };
      
      // Error handler
      reader.onerror = () => {
        clearTimeout(timeoutId);
        reject(new Error(`Failed to read file: ${file.name}`));
      };
      
      // Abort handler
      reader.onabort = () => {
        clearTimeout(timeoutId);
        reject(new Error(`File reading was aborted: ${file.name}`));
      };
      
      // Read file using appropriate method based on type
      try {
        if (file.type.startsWith('image/')) {
          reader.readAsDataURL(file);
        } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          reader.readAsDataURL(file);
        } else if (file.type.includes('spreadsheet') || 
                  file.type.includes('excel') || 
                  file.name.match(/\.xlsx?$/i) ||
                  file.name.match(/\.ods$/i)) {
          reader.readAsArrayBuffer(file);
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                  file.name.toLowerCase().endsWith('.docx')) {
          reader.readAsArrayBuffer(file);
        } else if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
          reader.readAsText(file);
        } else if (file.type === 'text/html' || 
                  file.name.toLowerCase().endsWith('.html') || 
                  file.name.toLowerCase().endsWith('.htm')) {
          reader.readAsText(file);
        } else if (file.type.startsWith('text/') || 
                  file.name.match(/\.(js|py|java|cpp|css|json|md|txt)$/i)) {
          reader.readAsText(file);
        } else {
          reader.readAsArrayBuffer(file);
        }
      } catch (readError) {
        clearTimeout(timeoutId);
        reject(new Error(`Error starting file read: ${readError.message}`));
      }
    });
  };
  
  // Process image file
  const processImage = (file, dataUrl, resolve, reject) => {
    const image = new Image();
    
    // Set up image load handlers
    image.onload = () => {
      resolve({
        contentType: 'image',
        dataUrl: dataUrl,
        width: image.width,
        height: image.height,
        isImage: true
      });
    };
    
    image.onerror = () => {
      reject(new Error(`Failed to load image: ${file.name}`));
    };
    
    // Start loading the image
    image.src = dataUrl;
  };
  
  // Process PDF file - just store the data URL
  const processPdf = (file, dataUrl, resolve) => {
    // Just store the PDF data URL without trying to parse it
    resolve({
      contentType: 'pdf',
      dataUrl: dataUrl,
      isPDF: true
    });
  };
  
  // Process CSV file - basic parsing without external libraries
  const processCsv = (file, content, resolve) => {
    // Perform basic CSV parsing
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      resolve({
        contentType: 'csv',
        content: content,
        isCSV: true
      });
      return;
    }
    
    try {
      // Parse header row (first line)
      const headers = lines[0].split(',').map(h => h.trim());
      
      // Parse data rows
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',');
        const row = {};
        
        headers.forEach((header, index) => {
          row[header] = index < values.length ? values[index].trim() : '';
        });
        
        rows.push(row);
      }
      
      resolve({
        contentType: 'csv',
        content: content,
        parsed: { headers, rows },
        isCSV: true
      });
    } catch (err) {
      // Return basic info if parsing fails
      resolve({
        contentType: 'csv',
        content: content,
        isCSV: true
      });
    }
  };
  
  // Process Excel file - minimal handling
  const processExcel = (file, buffer, resolve) => {
    // Just store the buffer without parsing
    resolve({
      contentType: 'excel',
      buffer: buffer,
      isExcel: true
    });
  };
  
  // Process DOCX file - minimal handling
  const processDocx = (file, buffer, resolve) => {
    // Just store the buffer 
    resolve({
      contentType: 'docx',
      buffer: buffer,
      isDocx: true,
      text: "[DOCX content will be processed on preview]"
    });
  };
  
  // Process HTML file - new handler
  const processHtml = (file, content, resolve) => {
    resolve({
      contentType: 'html',
      content: content,
      isHtml: true
    });
  };
  
  // Process text file
  const processText = (file, content, resolve) => {
    resolve({
      contentType: 'text',
      content: content,
      isText: true
    });
  };
  
  // Process binary file
  const processBinary = (file, buffer, resolve) => {
    resolve({
      contentType: 'binary',
      buffer: buffer,
      description: `[Binary content of ${file.type}]`,
      isBinary: true
    });
  };
  
  // Remove a file
  const removeFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
    setTotalSize(updatedFiles.reduce((sum, file) => sum + file.size, 0));
    
    // Notify parent component
    if (onFilesAdded) {
      console.log("FileUpload: Notifying parent of removed file, remaining:", updatedFiles.length);
      onFilesAdded(updatedFiles);
    }
  };
  
  // Remove all files
  const removeAllFiles = () => {
    setFiles([]);
    setTotalSize(0);
    
    // Notify parent component
    if (onFilesAdded) {
      console.log("FileUpload: Notifying parent of all files removed");
      onFilesAdded([]);
    }
  };
  
  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Calculate size percentage
  const sizePercentage = Math.min(100, (totalSize / maxTotalSize) * 100);
  
  // Get progress bar color based on percentage
  const getProgressColor = (percentage) => {
    if (percentage < 50) return '#4caf50';
    if (percentage < 80) return '#ff9800';
    return '#f44336';
  };

  // Get file type display
  const getFileTypeDisplay = (fileType, fileName) => {
    // First check filename extension
    if (fileName) {
      const extension = fileName.split('.').pop()?.toLowerCase();
      if (extension) {
        return extension.toUpperCase();
      }
    }
    
    // If no extension in filename, check MIME type
    if (!fileType) return '';
    
    // Handle common MIME types
    if (fileType === 'application/pdf') return 'PDF';
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
    if (fileType === 'application/msword') return 'DOC';
    if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'XLSX';
    if (fileType === 'application/vnd.ms-excel') return 'XLS';
    if (fileType === 'text/csv') return 'CSV';
    if (fileType === 'text/html') return 'HTML';
    
    // Default to the subtype part of the MIME type
    const typeParts = fileType.split('/');
    return typeParts.length > 1 ? typeParts[1].toUpperCase() : typeParts[0].toUpperCase();
  };

  return (
    <div className="file-upload-container">
      {/* Error message */}
      {error && (
        <div className="file-upload-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}
      
      {/* Upload area with new close button */}
      <div 
        className={`file-upload-area ${isDragging ? 'drag-active' : ''} ${isProcessing ? 'processing' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        {/* NEW: Close button */}
        <button 
          className="file-upload-close" 
          onClick={handleClose}
          aria-label="Close file upload"
        >
          ×
        </button>
        
        <input 
          type="file" 
          className="file-input" 
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
        />
        
        {/* Show processing UI or upload prompt */}
        {isProcessing ? (
          <div className="upload-processing">
            <div className="upload-spinner"></div>
            <p>Processing files...</p>
            {Object.keys(processingStatus).length > 0 && (
              <div className="processing-files-status">
                {Object.entries(processingStatus).map(([fileName, status]) => (
                  <div key={fileName} className="processing-file-item">
                    <div className="processing-file-name">{fileName}</div>
                    <div className="processing-file-progress-container">
                      <div 
                        className="processing-file-progress" 
                        style={{width: `${status.progress}%`}}
                      ></div>
                    </div>
                    <div className="processing-file-status">{status.status}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <label className="file-upload-label">
            <div className="upload-icon">📁</div>
            <div className="upload-text">
              <strong>Click to upload</strong> or drag and drop
            </div>
            <div className="upload-hint">
              Up to {maxFiles} files (8MB max per file, 20MB total)
              {isIncognitoMode && (
                <div className="incognito-mode-indicator">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C9.38 2 7.25 4.13 7.25 6.75C7.25 9.32 9.26 11.4 11.88 11.49C11.96 11.48 12.04 11.48 12.1 11.49C12.12 11.49 12.13 11.49 12.15 11.49C14.73 11.4 16.74 9.32 16.75 6.75C16.75 4.13 14.62 2 12 2Z" fill="#4A5568"/>
                    <path d="M17.08 14.15C14.29 12.29 9.74 12.29 6.93 14.15C5.66 15 4.96 16.15 4.96 17.38C4.96 18.61 5.66 19.75 6.92 20.59C8.32 21.53 10.16 22 12 22C13.84 22 15.68 21.53 17.08 20.59C18.34 19.74 19.04 18.6 19.04 17.36C19.03 16.13 18.34 14.99 17.08 14.15Z" fill="#4A5568"/>
                  </svg>
                  Incognito mode detected
                </div>
              )}
            </div>
            <div className="upload-formats">
              Images (5MB) • PDFs (8MB) • DOCX (5MB) • HTML (5MB) • Spreadsheets (5MB) • Text & Code (3MB)
            </div>
          </label>
        )}
      </div>
      
      {/* Size indicator */}
      {files.length > 0 && (
        <div className="upload-total-size">
          <div className="upload-size-info">
            <span>Total Size: {formatFileSize(totalSize)} of {formatFileSize(maxTotalSize)}</span>
            <span className="upload-size-percentage">{Math.round(sizePercentage)}%</span>
          </div>
          <div className="upload-size-progress-bar">
            <div 
              className="upload-size-progress" 
              style={{
                width: `${sizePercentage}%`,
                backgroundColor: getProgressColor(sizePercentage)
              }}
            ></div>
          </div>
        </div>
      )}
      
      {/* Selected files list with enhanced styling for View button */}
      {files.length > 0 && (
        <div className="selected-files">
          <div className="selected-files-header">
            <span>Selected Files ({files.length}/{maxFiles})</span>
            <button 
              className="remove-all-files" 
              onClick={(e) => {
                if (e && typeof e.preventDefault === 'function') e.preventDefault();
                if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                removeAllFiles();
              }}
            >
              Remove All
            </button>
          </div>
          <div className="files-list">
            {files.map((file, index) => (
              <div key={index} className="file-item">
                <div className="file-item-details">
                  <span className="file-icon">
                    {file.isImage ? '🖼️' : 
                     file.isPDF ? '📄' : 
                     file.isCSV || file.isExcel ? '📊' :
                     file.isDocx ? '📝' :
                     file.isHtml ? '🌐' :
                     file.isText ? '📝' : '📎'}
                  </span>
                  <div className="file-info">
                    <div className="file-name">{file.name}</div>
                    <div className="file-meta">
                      <span className="file-size">{formatFileSize(file.size)}</span>
                      <span className="file-meta-separator"> • </span>
                      <span className="file-type-label">{getFileTypeDisplay(file.type, file.name)}</span>
                      {file.error && (
                        <span className="file-error"> • Error: {file.error}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="file-actions">
                  <button 
                    className="file-preview-button" 
                    onClick={(e) => { 
                      if (e && typeof e.stopPropagation === 'function') {
                        e.stopPropagation();
                      }
                      openFilePreview(file);
                    }}
                  >
                    View
                  </button>
                  <button 
                    className="remove-file" 
                    onClick={(e) => {
                      if (e && typeof e.stopPropagation === 'function') {
                        e.stopPropagation();
                      }
                      removeFile(index);
                    }}
                    aria-label={`Remove ${file.name}`}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File preview dialog */}
      {previewFile && (
        <FilePreview 
          file={previewFile}
          onClose={closeFilePreview}
        />
      )}
    </div>
  );
}

export default FileUpload;